/**
 * Client Portal - Contract Detail
 * Shows contract details and amortization schedule for external users
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet } from '../../utils/apiClient';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import useAsyncData from '../../hooks/useAsyncData';
import { formatCurrency as formatCurrencyBase, formatDate } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 180, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight="medium">
      {value || '-'}
    </Typography>
  </Box>
);

const ClientContractDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;
  const formatCurrency = (amount, currency) => formatCurrencyBase(amount, currency, dp);
  const { data: contractData, loading, error } = useAsyncData(
    async () => {
      const [contractRes, scheduleRes] = await Promise.all([
        apiGet(`/client-portal/my-contracts/${id}`),
        apiGet(`/client-portal/my-contracts/${id}/schedule`),
      ]);
      return {
        contract: contractRes.data || contractRes,
        schedule: scheduleRes.data || scheduleRes || [],
      };
    },
    { defaultValue: { contract: null, schedule: [] }, fetchOnMount: !!id, deps: [id], extractData: false }
  );

  const { contract, schedule } = contractData;

  // Determine flow type from narration text or flow_direction from API
  const getFlowType = (row) => {
    if (row.flow_direction === 1) {
      // Inflow, but check if it's an accrual
      if (row.narration_text && row.narration_text.includes('Accrual')) return 'accrual';
      return 'inflow';
    }
    if (row.flow_direction === -1) return 'outflow';
    // Fallback to narration text matching
    const narration = row.narration_text || '';
    if (narration.includes('Accrual')) return 'accrual';
    if (narration.includes('Drawdown') || narration.includes('Injection') || narration.includes('Opening Balance')) return 'inflow';
    if (narration.includes('Payment') || narration.includes('Fee') || narration.includes('Withdrawal')) return 'outflow';
    return 'neutral';
  };

  const getFlowLabel = (row) => {
    const flowType = getFlowType(row);
    if (flowType === 'inflow') return { label: 'IN', color: 'info' };
    if (flowType === 'outflow') return { label: 'OUT', color: 'error' };
    if (flowType === 'accrual') return { label: 'ACC', color: 'warning' };
    return { label: '-', color: 'default' };
  };

  const handleExportSchedule = () => {
    // Create CSV content
    const headers = ['Period', 'Payment Date', 'Flow', 'Narration', 'Interest', 'Capital', 'Payment', 'Balance'];
    const rows = schedule.map(row => [
      row.sequence_number,
      formatDate(row.payment_date),
      getFlowLabel(row).label,
      row.narration_text || '',
      row.interest_amount?.toFixed(2) || '0.00',
      row.capital_amount?.toFixed(2) || '0.00',
      row.payment_amount?.toFixed(2) || '0.00',
      row.outstanding_balance?.toFixed(2) || '0.00',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schedule-${contract?.reference || id}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/client-portal/contracts')}
          sx={{ mt: 2 }}
        >
          Back to Contracts
        </Button>
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box>
        <Alert severity="warning">Contract not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/client-portal/contracts')}
          sx={{ mt: 2 }}
        >
          Back to Contracts
        </Button>
      </Box>
    );
  }

  // Calculate totals from schedule
  const totals = schedule.reduce((acc, row) => ({
    interest: acc.interest + (row.interest_amount || 0),
    capital: acc.capital + (row.capital_amount || 0),
    payment: acc.payment + (row.payment_amount || 0),
  }), { interest: 0, capital: 0, payment: 0 });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/client-portal/contracts')}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" component="h1">
              Contract {contract.user_reference || contract.reference}
            </Typography>
            {contract.user_reference && (
              <Typography variant="body2" color="textSecondary">
                {contract.reference}
              </Typography>
            )}
          </Box>
          <Chip
            label={contract.status}
            color={contract.status === 'Active' ? 'success' : 'default'}
          />
        </Box>
      </Box>
      {/* Contract Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Capital Amount</Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(contract.capital_amount, contract.currency_code)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Interest Rate</Typography>
              <Typography variant="h5" fontWeight="bold">
                {contract.interest_rate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {contract.rate_type}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Interest</Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {formatCurrency(totals.interest, contract.currency_code)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Remaining Balance</Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(schedule[schedule.length - 1]?.outstanding_balance || 0, contract.currency_code)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Contract Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Contract Details</Typography>
        <Grid container spacing={4}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            {contract.user_reference && (
              <InfoRow label="External Reference" value={contract.user_reference} />
            )}
            <InfoRow label="Reference" value={contract.reference} />
            <InfoRow label="Product Type" value={contract.product_type} />
            <InfoRow label="Investor" value={contract.investor_name} />
            <InfoRow label="Currency" value={contract.currency_code} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <InfoRow label="Start Date" value={formatDate(contract.deposit_date)} />
            <InfoRow label="Maturity Date" value={formatDate(contract.maturity_date)} />
            <InfoRow label="Interest Frequency" value={contract.interest_frequency} />
            <InfoRow label="Capital Frequency" value={contract.capital_frequency} />
          </Grid>
        </Grid>
      </Paper>
      {/* Amortization Schedule */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Payment Schedule</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportSchedule}
            disabled={schedule.length === 0}
          >
            Export CSV
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Flow</TableCell>
                <TableCell>Narration</TableCell>
                <TableCell align="right">Interest</TableCell>
                <TableCell align="right">Capital</TableCell>
                <TableCell align="right">Payment</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedule.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No payment schedule available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {schedule.map((row) => {
                    const flow = getFlowLabel(row);
                    const flowType = getFlowType(row);
                    return (
                      <TableRow
                        key={row.sequence_number}
                        sx={{
                          bgcolor: new Date(row.payment_date) < new Date() ? 'action.hover' : 'inherit'
                        }}
                      >
                        <TableCell>{row.sequence_number}</TableCell>
                        <TableCell>{formatDate(row.payment_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={flow.label}
                            color={flow.color}
                            size="small"
                            sx={{ fontWeight: 'bold', minWidth: 44 }}
                          />
                        </TableCell>
                        <TableCell>
                          {row.narration_text ? (
                            <Chip
                              label={row.narration_text}
                              variant="outlined"
                              size="small"
                              color={flow.color !== 'default' ? flow.color : undefined}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{
                          color: flowType === 'outflow' ? 'error.main' : flowType === 'inflow' ? 'info.main' : 'inherit'
                        }}>
                          {flowType === 'outflow' && row.interest_amount
                            ? `(${formatCurrency(Math.abs(row.interest_amount), contract.currency_code)})`
                            : formatCurrency(row.interest_amount, contract.currency_code)}
                        </TableCell>
                        <TableCell align="right" sx={{
                          color: flowType === 'outflow' ? 'error.main' : flowType === 'inflow' ? 'info.main' : 'inherit'
                        }}>
                          {formatCurrency(row.capital_amount, contract.currency_code)}
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 'medium',
                          color: flowType === 'outflow' ? 'error.main' : flowType === 'inflow' ? 'info.main' : 'inherit'
                        }}>
                          {flowType === 'outflow' && row.payment_amount
                            ? `(${formatCurrency(Math.abs(row.payment_amount), contract.currency_code)})`
                            : formatCurrency(row.payment_amount, contract.currency_code)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(row.outstanding_balance, contract.currency_code)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Totals Row */}
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    <TableCell colSpan={4}>
                      <Typography fontWeight="bold">Totals</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {formatCurrency(totals.interest, contract.currency_code)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {formatCurrency(totals.capital, contract.currency_code)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {formatCurrency(totals.payment, contract.currency_code)}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ClientContractDetail;
